// ============================================================================
// MÓDULO 7 - UNIDAD 5: WHAT I'M WEARING
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Present Continuous (BE + verb-ING)
// Spelling rules: Double consonant, -ie to -y, drop silent e
// Vocabulario: Clothes, materials, patterns
// Contextos: Park, office, police, shopping, fashion, weather
// ============================================================================

export const M7_UNIT_5 = {
  id: "m7-u5",
  title: "Unit 5: What I'm Wearing",
  topic: "Master present continuous, describe clothing with materials and patterns, and talk about what people are wearing right now!",
  
  grammarImage: "/images/grammar/m7-u5-present-continuous.jpg",
  grammarText: `
    <h3>Present Continuous - What I'm Wearing</h3>
    
    <div class="grammar-rule">
      <h4>Present Continuous Formation</h4>
      <p>Used to describe actions happening RIGHT NOW:</p>
      <p><strong>Structure:</strong> Subject + BE (am/is/are) + Verb-ING</p>
      
      <div class="examples">
        <p><strong>Examples:</strong></p>
        <ul>
          <li>I <strong>am wearing</strong> a blue shirt</li>
          <li>She <strong>is trying on</strong> a dress</li>
          <li>They <strong>are shopping</strong> for new shoes</li>
        </ul>
      </div>
    </div>

    <div class="spelling-rules">
      <h4>Spelling Rules for -ING</h4>
      
      <p><strong>1. Double Consonant (CVC pattern):</strong></p>
      <ul>
        <li>shop → shop<strong>p</strong>ing</li>
        <li>put → put<strong>t</strong>ing</li>
        <li>sit → sit<strong>t</strong>ing</li>
      </ul>
      
      <p><strong>2. Drop Silent E:</strong></p>
      <ul>
        <li>make → mak<strike>e</strike> + ing = making</li>
        <li>take → tak<strike>e</strike> + ing = taking</li>
        <li>have → hav<strike>e</strike> + ing = having</li>
      </ul>
      
      <p><strong>3. Change -IE to -Y:</strong></p>
      <ul>
        <li>tie → t<strike>ie</strike> + <strong>y</strong>ing = tying</li>
        <li>lie → l<strike>ie</strike> + <strong>y</strong>ing = lying</li>
        <li>die → d<strike>ie</strike> + <strong>y</strong>ing = dying</li>
      </ul>
      
      <p><strong>4. Simple +ING (most verbs):</strong></p>
      <ul>
        <li>wear → wearing</li>
        <li>look → looking</li>
        <li>carry → carrying</li>
      </ul>
    </div>

    <div class="vocabulary-section">
      <h4>Clothing Vocabulary</h4>
      
      <p><strong>Basic Clothes:</strong> shirt, pants, dress, skirt, shoes, jacket</p>
      <p><strong>Formal/Smart:</strong> suit, tie, blazer, dress shirt</p>
      <p><strong>Casual:</strong> jeans, t-shirt, hoodie, sneakers, sweater</p>
      <p><strong>Weather:</strong> raincoat, umbrella, coat, scarf, gloves</p>
      
      <p><strong>Materials:</strong> cotton, silk, leather, denim, wool</p>
      <p><strong>Patterns:</strong> striped (rayas), checked (cuadros), polka dot (lunares), plain</p>
    </div>
  `,

  iconType: "shirt",

  sets: [
    // ========================================================================
    // SET 1: DAY AT THE PARK 🌳 - BASIC CLOTHES + INTRO
    // ========================================================================
    {
      id: "m7-u5-s1",
      title: "Set 1: Day at the Park 🌳",
      context: `**Saturday at Central Park**

It's a beautiful sunny Saturday. You're at the park with friends, watching people enjoy the day. Everyone is wearing different outfits suitable for outdoor activities.`,
      
      questions: [
        {
          type: "mc",
          q: "Look at that woman jogging! 'She ___ a pink t-shirt and black leggings.'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          tip: "⏰ PRESENT CONTINUOUS!\n\nFor actions happening RIGHT NOW:\n✅ 'She IS wearing' (present continuous)\n❌ 'She wears' (present simple - habits)\n\nStructure: BE + verb-ING\n• I am wearing\n• She/He is wearing\n• They are wearing\n\nUse present continuous when describing what someone is wearing at this moment! 🎯",
          successtip: "✓ RIGHT NOW ACTION!\n'Is wearing' = perfect for describing current clothing! 👕"
        },
        {
          type: "blank",
          q: "A family is having a picnic. The dad ___ a striped polo shirt. (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ POLO SHIRT!\nFun fact: 'Polo shirt' was named after the sport - originally worn by polo players! 🐴👕"
        },
        {
          type: "mc",
          q: "You see kids playing soccer. 'They ___ colorful jerseys and sneakers.'",
          options: [
            "are wearing",
            "is wearing",
            "wear"
          ],
          a: "are wearing",
          successtip: "✓ PLURAL SUBJECT!\n'They are wearing' = correct for multiple people! ⚽👟"
        },
        {
          type: "order",
          q: "Traduce: Una mujer está usando un vestido azul hermoso",
          a: "A woman is wearing a beautiful blue dress",
          successtip: "✓ PRESENT CONTINUOUS!\nBonus: Remember adjective order - beautiful (opinion) before blue (color)! 👗💙"
        },
        {
          type: "mc",
          q: "Two teenagers on skateboards. 'One ___ a black hoodie, the other ___ a denim jacket.'",
          options: [
            "is wearing / is wearing",
            "are wearing / are wearing",
            "wears / wears"
          ],
          a: "is wearing / is wearing",
          successtip: "✓ BOTH SINGULAR!\n'One is... the other is...' = each person singular! 🛹"
        },
        {
          type: "blank",
          q: "An elderly couple is walking. She ___ a cardigan over her dress. (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ CARDIGAN!\nStyle tip: Cardigans = perfect layering piece for unpredictable weather! 🧥"
        },
        {
          type: "mc",
          q: "You notice someone reading. 'That man ___ glasses and a casual shirt.'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          successtip: "✓ ACCESSORIES TOO!\n'Wearing glasses' = yes, we use 'wear' for accessories like glasses, hats, jewelry! 👓"
        },
        {
          type: "mc",
          q: "A dog walker passes by. 'She ___ comfortable sneakers and jeans.'",
          options: [
            "is wearing",
            "are wearing",
            "wear"
          ],
          a: "is wearing",
          successtip: "✓ PRACTICAL OUTFIT!\nCultural note: Sneakers + jeans = classic American casual look! 👟👖🇺🇸"
        },
        {
          type: "order",
          q: "Traduce: Los niños están usando sombreros y protector solar",
          a: "The children are wearing hats and sunscreen",
          successtip: "✓ SUN PROTECTION!\nNote: 'Sunscreen' isn't worn like clothes, but we say 'wearing sunscreen' idiomatically! ☀️🧴"
        },
        {
          type: "mc",
          q: "Summary: At the park right now, everyone ___ different clothes for outdoor fun.",
          options: [
            "is wearing",
            "are wearing",
            "wears"
          ],
          a: "is wearing",
          tip: "👥 COLLECTIVE EVERYONE!\n\n'Everyone' = singular!\n✅ Everyone IS wearing\n❌ Everyone ARE wearing\n\nEven though it refers to many people, 'everyone' is grammatically singular!\n\nSame rule for:\n• Everybody IS\n• Someone IS\n• Anyone IS\n\nCollective pronouns = singular verb! 🎯",
          successtip: "✓ TRICKY GRAMMAR!\n'Everyone' looks plural but acts singular - common mistake caught! 📝"
        }
      ]
    },

    // ========================================================================
    // SET 2: PHONE CALL - OFFICE EMERGENCY 📞💼
    // ========================================================================
    {
      id: "m7-u5-s2",
      title: "Set 2: Office Emergency Call 📞💼",
      context: `**📞 Ring ring...**

"Hi Mike, it's Jennifer. There's an emergency client meeting in 15 minutes! Where are you?"

"I'm still at home! What should I wear?"

"Professional! Describe what you're wearing right now..."`,
      
      questions: [
        {
          type: "mc",
          q: "Mike responds: 'I ___ my pajamas! I just woke up!'",
          options: [
            "am wearing",
            "is wearing",
            "wear"
          ],
          a: "am wearing",
          successtip: "✓ FIRST PERSON!\n'I am wearing' = describing your current outfit! Pajamas at noon = uh oh! 😅🛏️"
        },
        {
          type: "blank",
          q: "Jennifer: 'Put on a suit! What color suit ___ you ___?' (wear + -ing)",
          a: "are, wearing",
          accept: { ignoreCase: true, alts: ["are wearing", "are / wearing"] },
          successtip: "✓ QUESTION FORM!\nQuestion structure: BE + subject + verb-ING = 'Are you wearing?' ❓"
        },
        {
          type: "mc",
          q: "Mike: 'I ___ my navy blue suit right now. Is that okay?'",
          options: [
            "am putting on",
            "put on",
            "putting on"
          ],
          a: "am putting on",
          tip: "👔 PHRASAL VERB + CONTINUOUS!\n\n'Put on' = phrasal verb for dressing\n\nPresent continuous:\n✅ 'I AM putting on my suit' (happening now)\n❌ 'I putting on' (missing BE)\n❌ 'I put on' (simple present - habit)\n\nPhrasal verbs work the same in continuous:\nBE + putting on (verb-ING + particle) 🎯",
          successtip: "✓ ACTION IN PROGRESS!\n'Putting on' = the act of dressing RIGHT NOW! 👔⏰"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué corbata estás usando con ese traje?",
          a: "What tie are you wearing with that suit?",
          successtip: "✓ FORMAL ACCESSORY!\nBusiness tip: Tie color matters - red = power, blue = trust, yellow = creative! 👔🎨"
        },
        {
          type: "mc",
          q: "Mike: 'I ___ a red tie. Too bold?'",
          options: [
            "am wearing",
            "wear",
            "wearing"
          ],
          a: "am wearing",
          successtip: "✓ POWER TIE!\nRed tie = 'power tie' in business - bold choice for important meetings! 🔴👔💼"
        },
        {
          type: "blank",
          q: "Jennifer: 'Perfect! What about shoes? ___ you ___ dress shoes?' (wear + -ing)",
          a: "Are, wearing",
          accept: { ignoreCase: true, alts: ["are wearing", "are / wearing"] },
          successtip: "✓ FOOTWEAR CHECK!\nDress code essential: Dress shoes = formal, sneakers = casual. Match your outfit level! 👞"
        },
        {
          type: "mc",
          q: "Mike: 'Yes, I ___ my black leather dress shoes.'",
          options: [
            "am wearing",
            "wear",
            "am wear"
          ],
          a: "am wearing",
          successtip: "✓ COMPLETE OUTFIT!\nLeather dress shoes + navy suit = classic professional combination! 👞👔✨"
        },
        {
          type: "mc",
          q: "Jennifer: 'Great! And I ___ my gray blazer with black pants. We'll look professional!'",
          options: [
            "am wearing",
            "is wearing",
            "wear"
          ],
          a: "am wearing",
          successtip: "✓ BUSINESS WOMAN STYLE!\n'Blazer + pants' = modern professional women's alternative to a dress! 👩‍💼"
        },
        {
          type: "order",
          q: "Traduce: Él se está poniendo una camisa blanca ahora mismo",
          a: "He is putting on a white shirt right now",
          successtip: "✓ URGENT DRESSING!\n'Right now' emphasizes the present continuous - happening this very moment! ⏰👔"
        },
        {
          type: "mc",
          q: "End of call: 'Okay, we ___ professional outfits. See you at the meeting!'",
          options: [
            "are wearing",
            "is wearing",
            "wear"
          ],
          a: "are wearing",
          successtip: "✓ TEAM READY!\n'We are wearing' = both people prepared! Professional crisis averted! 💼✅"
        }
      ]
    },

    // ========================================================================
    // SET 3: POLICE REPORT 👮‍♀️🚨
    // ========================================================================
    {
      id: "m7-u5-s3",
      title: "Set 3: Police Report - Witness Statement 👮‍♀️",
      context: `**At the Police Station**

Officer: "Thank you for coming in. You witnessed the incident at the convenience store. Can you describe what the suspect was wearing?"

Witness: "Yes, officer. I remember clearly..."`,
      
      questions: [
        {
          type: "mc",
          q: "Witness: 'The suspect ___ a black hoodie when he entered the store.'",
          options: [
            "was wearing",
            "is wearing",
            "wearing"
          ],
          a: "was wearing",
          tip: "⏰ PAST CONTINUOUS!\n\nFor actions happening at a specific time in the past:\n✅ 'He WAS wearing' (past continuous)\n❌ 'He IS wearing' (present - wrong time)\n\nStructure: WAS/WERE + verb-ING\n• I/He/She was wearing\n• We/They were wearing\n\nUse past continuous for past descriptions! 🎯",
          successtip: "✓ PAST DESCRIPTION!\n'Was wearing' = what he had on at that moment in the past! 👮‍♀️"
        },
        {
          type: "blank",
          q: "Officer: 'What color ___ the hoodie?'",
          a: "was",
          accept: { ignoreCase: true },
          successtip: "✓ COLOR QUESTION!\nPolice reports always ask for color first - most visible identifying feature! 🎨"
        },
        {
          type: "mc",
          q: "Witness: 'Black. And he ___ dark blue jeans, I think.'",
          options: [
            "was wearing",
            "were wearing",
            "is wearing"
          ],
          a: "was wearing",
          successtip: "✓ FULL OUTFIT!\nJeans = 'denim pants' - originally workwear, now universal casual wear! 👖"
        },
        {
          type: "order",
          q: "Traduce: El sospechoso estaba usando zapatillas blancas",
          a: "The suspect was wearing white sneakers",
          successtip: "✓ FOOTWEAR DETAIL!\nWhite sneakers = very distinctive, easier to spot and remember! 👟✨"
        },
        {
          type: "mc",
          q: "Officer: '___ he ___ anything else? Accessories?'",
          options: [
            "Was / wearing",
            "Is / wearing",
            "Were / wearing"
          ],
          a: "Was / wearing",
          successtip: "✓ FOLLOW-UP!\nAccessories = important details: hats, glasses, jewelry, watches, bags! 🎒👓"
        },
        {
          type: "blank",
          q: "Witness: 'Yes! He ___ a red baseball cap and sunglasses.' (wear + -ing, past)",
          a: "was wearing",
          accept: { ignoreCase: true },
          successtip: "✓ DISGUISE ITEMS!\nBaseball cap + sunglasses = classic attempt to hide identity! 🧢🕶️"
        },
        {
          type: "mc",
          q: "Officer: 'And the cashier - what ___ she ___?'",
          options: [
            "was / wearing",
            "is / wearing",
            "were / wearing"
          ],
          a: "was / wearing",
          successtip: "✓ WITNESS DETAIL!\nGood witnesses remember everyone's clothing - helps verify the story! 👩‍💼"
        },
        {
          type: "mc",
          q: "Witness: 'The cashier ___ the store uniform - a blue polo shirt and black pants.'",
          options: [
            "was wearing",
            "is wearing",
            "were wearing"
          ],
          a: "was wearing",
          successtip: "✓ UNIFORM!\nUniforms help identify employees - standard in retail, restaurants, security! 👕🏪"
        },
        {
          type: "order",
          q: "Traduce: ¿Estaba usando algún tipo de guantes?",
          a: "Was he wearing any kind of gloves?",
          successtip: "✓ CRITICAL DETAIL!\nGloves = no fingerprints! Very important detail in police reports! 🧤🔍"
        },
        {
          type: "mc",
          q: "Witness: 'Actually, yes. He ___ black gloves. I remember thinking that was suspicious.'",
          options: [
            "was wearing",
            "is wearing",
            "wearing"
          ],
          a: "was wearing",
          successtip: "✓ EVIDENCE!\nBlack gloves in summer = major suspicious detail! Good observation! 🕵️‍♀️"
        }
      ]
    },

    // ========================================================================
    // SET 4: SHOPPING FOR QUALITY 🛍️ - MATERIALS
    // ========================================================================
    {
      id: "m7-u5-s4",
      title: "Set 4: Shopping - Materials Matter 🛍️",
      context: `**At the Clothing Store**

Customer: "Excuse me, I'm looking for a new jacket."

Sales Clerk: "Certainly! We have different materials. Let me show you what we're selling today..."`,
      
      questions: [
        {
          type: "match",
          q: "Match clothing materials with their characteristics:",
          pairs: [
            { left: "Leather", right: "Animal hide - durable, stylish, expensive" },
            { left: "Cotton", right: "Plant fiber - breathable, comfortable, casual" },
            { left: "Silk", right: "Luxurious fabric - smooth, elegant, formal" },
            { left: "Denim", right: "Heavy cotton - durable, casual, for jeans" }
          ],
          successtip: "✓ MATERIALS MASTERED!\nFun fact: Denim originally comes from 'Nîmes' (French city) = 'de Nîmes' → denim! 🇫🇷👖"
        },
        {
          type: "mc",
          q: "Sales Clerk: 'This jacket ___ of genuine leather. Very high quality.'",
          options: [
            "is made",
            "is making",
            "makes"
          ],
          a: "is made",
          tip: "🏭 'MADE OF' STRUCTURE!\n\n'Be made of' = passive voice for materials\n✅ 'This is made of leather'\n❌ 'This makes of leather'\n\nStructure: BE + made of + material\n• This jacket is made of leather\n• These pants are made of cotton\n\nNOT continuous tense here!\n'Made' is past participle, not present continuous! 🎯",
          successtip: "✓ MATERIAL DESCRIPTION!\n'Made of + material' = standard way to describe fabric/material! 🧥"
        },
        {
          type: "blank",
          q: "Customer: 'What about this shirt? What material ___?'",
          a: "is it",
          accept: { ignoreCase: true, alts: ["is this", "is it made of"] },
          successtip: "✓ MATERIAL QUESTION!\nCommon shopping question: 'What's it made of?' = asking about material! 👔"
        },
        {
          type: "mc",
          q: "Sales Clerk: 'That one is pure ___ - very breathable for summer.'",
          options: [
            "cotton",
            "leather",
            "wool"
          ],
          a: "cotton",
          successtip: "✓ SUMMER FABRIC!\nCotton = best for hot weather - absorbs sweat, allows air flow! ☀️👕"
        },
        {
          type: "order",
          q: "Traduce: Esta bufanda está hecha de seda pura",
          a: "This scarf is made of pure silk",
          successtip: "✓ LUXURY ITEM!\nSilk scarves = classic luxury accessory, originally from China! 🧣✨🇨🇳"
        },
        {
          type: "mc",
          q: "Customer tries on pants. 'These jeans are ___. They feel sturdy!'",
          options: [
            "denim",
            "silk",
            "cotton"
          ],
          a: "denim",
          successtip: "✓ CLASSIC DENIM!\nDenim = heavy cotton fabric, treated to be durable. Invented by Levi Strauss! 👖🇺🇸"
        },
        {
          type: "blank",
          q: "Sales Clerk: 'For winter, I recommend ___ - it keeps you warm.' (material from sheep)",
          a: "wool",
          accept: { ignoreCase: true },
          successtip: "✓ WARM MATERIAL!\nWool = from sheep, natural insulation. Also: cashmere (from goats) = luxury wool! 🐑🧶"
        },
        {
          type: "mc",
          q: "Customer: 'This sweater ___ so soft! Is it cashmere?'",
          options: [
            "feels",
            "is feeling",
            "feel"
          ],
          a: "feels",
          tip: "⚠️ STATIVE VERBS!\n\n'Feel' (when meaning 'have a texture') is STATIVE:\n✅ 'This feels soft' (simple present)\n❌ 'This is feeling soft' (WRONG!)\n\nStative verbs describe STATES, not actions:\n• Feel (texture)\n• Look (appearance)\n• Smell (odor)\n• Taste (flavor)\n\nThese DON'T use continuous normally! 🎯",
          successtip: "✓ TEXTURE!\nStative verb alert: 'feels' (not 'is feeling') for describing texture! 🧶"
        },
        {
          type: "order",
          q: "Traduce: ¿Estas botas están hechas de cuero?",
          a: "Are these boots made of leather?",
          successtip: "✓ LEATHER BOOTS!\nLeather boots = durable and stylish, but need care (polish, waterproofing)! 👢✨"
        },
        {
          type: "mc",
          q: "Final purchase: 'I'll take the cotton shirt and the leather jacket. They ___ perfect quality!'",
          options: [
            "are",
            "are being",
            "is"
          ],
          a: "are",
          successtip: "✓ QUALITY CHECK!\n'They are perfect' (stative) not continuous - describes state, not action! 🛍️✅"
        }
      ]
    },

    // ========================================================================
    // SET 5: FASHION MAGAZINE - PATTERNS 📰
    // ========================================================================
    {
      id: "m7-u5-s5",
      title: "Set 5: Fashion Magazine - Patterns 📰",
      context: `**STYLE WEEKLY MAGAZINE**

"This Season's Hottest Patterns"

Our fashion correspondent is at Paris Fashion Week, describing what models are wearing on the runway right now...`,
      
      questions: [
        {
          type: "match",
          q: "Match pattern names with descriptions:",
          pairs: [
            { left: "Striped", right: "Lines running across the fabric (rayas)" },
            { left: "Checked", right: "Square pattern like a chessboard (cuadros)" },
            { left: "Polka dot", right: "Round dots on fabric (lunares)" },
            { left: "Plain", right: "Solid color, no pattern (liso)" }
          ],
          successtip: "✓ PATTERN VOCABULARY!\nFashion tip: Plain/solid pieces = versatile base for mixing patterns! 👗🎨"
        },
        {
          type: "mc",
          q: "Reporter: 'Model número uno ___ a beautiful striped dress in blue and white!'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          successtip: "✓ LIVE COMMENTARY!\nFashion shows = perfect for present continuous - describing runway RIGHT NOW! 🎤✨"
        },
        {
          type: "blank",
          q: "Reporter: 'And now another model ___ out in a stunning checked blazer!' (come + -ing)",
          a: "is coming",
          accept: { ignoreCase: true, alts: ["'s coming"] },
          successtip: "✓ ACTION VERB!\n'Coming out' = walking onto the runway. Fashion shows = theatrical! 🚶‍♀️✨"
        },
        {
          type: "mc",
          q: "Reporter: 'She ___ it with plain black pants. Very chic!'",
          options: [
            "is pairing",
            "pairs",
            "pairing"
          ],
          a: "is pairing",
          successtip: "✓ STYLING TIP!\n'Pair with' = fashion term for 'combine with'. Mix patterns + plain = classic! 🎨👔"
        },
        {
          type: "order",
          q: "Traduce: El modelo está usando una camisa de lunares",
          a: "The model is wearing a polka dot shirt",
          successtip: "✓ POLKA DOTS!\nRetro pattern from 1920s-50s, now timeless! Associated with vintage style! 🔴⚪✨"
        },
        {
          type: "mc",
          q: "Reporter: 'Wait! A model ___ a completely plain red suit. Bold choice!'",
          options: [
            "is wearing",
            "wears",
            "wear"
          ],
          a: "is wearing",
          successtip: "✓ SOLID COLOR!\n'Plain' in fashion = solid color, no pattern. Sometimes 'solid' is also used! 🔴"
        },
        {
          type: "blank",
          q: "Reporter: 'The designer ___ vertical stripes this season - very slimming!' (use + -ing)",
          a: "is using",
          accept: { ignoreCase: true, alts: ["'s using"] },
          successtip: "✓ FASHION SECRET!\nVertical stripes = make you look taller/slimmer. Horizontal = wider. Styling trick! 📏👗"
        },
        {
          type: "mc",
          q: "Reporter: 'I ___ at least five different patterns on the runway today!'",
          options: [
            "have seen",
            "am seeing",
            "see"
          ],
          a: "have seen",
          tip: "⏰ TENSE CHOICE!\n\n'Have seen' = present perfect (result of today's viewing)\n\nWhy not present continuous?\n'See' in the meaning of 'perceive with eyes' is usually STATIVE:\n✅ 'I see it' (simple)\n✅ 'I have seen five patterns' (perfect - experience)\n❌ 'I am seeing patterns' (wrong for passive viewing)\n\nBUT 'see' CAN be continuous when it means 'meet':\n✅ 'I'm seeing my doctor tomorrow'\n\nContext matters! 🎯",
          successtip: "✓ EXPERIENCE!\nPresent perfect 'have seen' = counting today's experience! 👁️✅"
        },
        {
          type: "mc",
          q: "Fashion tip: 'If you ___ stripes, pair them with plain pieces.'",
          options: [
            "wear",
            "are wearing",
            "wearing"
          ],
          a: "wear",
          successtip: "✓ GENERAL ADVICE!\nGeneral rules = simple present ('wear'), not continuous! This is styling advice! 💡"
        },
        {
          type: "order",
          q: "Traduce: Ella está mezclando rayas y cuadros - muy atrevido",
          a: "She is mixing stripes and checks - very bold",
          successtip: "✓ FASHION RISK!\nMixing patterns = advanced fashion! Usually avoid, but can work if done right! 🎨✨"
        }
      ]
    },

    // ========================================================================
    // SET 6: GETTING READY - MORNING ROUTINE ⏰
    // ========================================================================
    {
      id: "m7-u5-s6",
      title: "Set 6: Getting Ready - Spelling Rules ⏰",
      context: `**6:45 AM - Getting Ready for Work**

You're running late! Let's describe what you're doing RIGHT NOW as you rush to get dressed...`,
      
      questions: [
        {
          type: "mc",
          q: "'Put' → 'putting' doubles the T. Why?",
          options: [
            "CVC pattern: Consonant-Vowel-Consonant = double final consonant",
            "All verbs ending in T double it",
            "Random rule"
          ],
          a: "CVC pattern: Consonant-Vowel-Consonant = double final consonant",
          tip: "✍️ DOUBLING RULE!\n\nCVC = Consonant + Vowel + Consonant (one syllable, stressed)\n\nDouble the final consonant before -ing:\n• put (p-u-t) → putting\n• shop (sh-o-p) → shopping\n• sit (s-i-t) → sitting\n• run (r-u-n) → running\n\nBUT NOT when:\n• Two vowels: sleep → sleeping (not slepping)\n• Two consonants: jump → jumping (not jumpping)\n• Unstressed: open → opening (not openning)\n\nThis is a key spelling rule! 🎯",
          successtip: "✓ SPELLING PATTERN!\nCVC words: shop, stop, put, sit, run, get, cut = all double the consonant! ✍️"
        },
        {
          type: "blank",
          q: "'I'm ___ on my shirt!' (put + -ing, remember to double!)",
          a: "putting",
          accept: { ignoreCase: true },
          successtip: "✓ DOUBLED!\n'Putting' = double T because 'put' is CVC! Other examples: getting, sitting, running! 🏃"
        },
        {
          type: "mc",
          q: "'Make' ends in silent E. 'I'm ___ breakfast while getting dressed!'",
          options: [
            "making",
            "makeing",
            "mak-ing"
          ],
          a: "making",
          tip: "✍️ SILENT E RULE!\n\nVerbs ending in silent E → drop the E before -ing:\n• make → mak(e) + ing = making\n• take → tak(e) + ing = taking\n• have → hav(e) + ing = having\n• come → com(e) + ing = coming\n• write → writ(e) + ing = writing\n\n❌ makeing, takeing = WRONG!\n✅ making, taking = CORRECT!\n\nDrop that silent E! 🎯",
          successtip: "✓ SILENT E DROPPED!\n'Making' = most common example. Almost all E-ending verbs follow this rule! 📝"
        },
        {
          type: "blank",
          q: "'I'm ___ my tie.' (tie + -ing, -ie changes to -y!)",
          a: "tying",
          accept: { ignoreCase: true },
          successtip: "✓ TRICKY SPELLING!\n'Tying' = special rule! Other -ie verbs: lying, dying. Rare but important! 👔"
        },
        {
          type: "mc",
          q: "Why is it 'tying' not 'tieing'?",
          options: [
            "Verbs ending in -ie change to -y before -ing",
            "Because it's easier to spell",
            "It's just irregular"
          ],
          a: "Verbs ending in -ie change to -y before -ing",
          successtip: "✓ RARE RULE!\nOnly a few verbs end in -ie: tie, lie, die. All follow same pattern: -ying! ✍️"
        },
        {
          type: "order",
          q: "Traduce: Ella se está poniendo sus zapatos ahora",
          a: "She is putting on her shoes now",
          successtip: "✓ 'PUTTING ON'!\nPut on = phrasal verb. Present continuous: putting on (double T + particle)! 👟"
        },
        {
          type: "blank",
          q: "'I'm ___ for my keys!' (look + -ing, simple rule)",
          a: "looking",
          accept: { ignoreCase: true },
          successtip: "✓ SIMPLE +ING!\n'Look' doesn't need doubling (two consonants) or E dropping. Just add -ing! 🔍"
        },
        {
          type: "mc",
          q: "'Shop' → 'shopping' because:",
          options: [
            "CVC pattern requires doubling (sh-o-p)",
            "All words with 'op' double the P",
            "It looks better with double P"
          ],
          a: "CVC pattern requires doubling (sh-o-p)",
          successtip: "✓ CVC AGAIN!\n'Shopping' = classic example. Same pattern: stopping, hopping, dropping! 🛍️"
        },
        {
          type: "mc",
          q: "Which is CORRECT? 'I'm ___ new clothes today!'",
          options: [
            "buying",
            "buying (double Y)",
            "bu-ying"
          ],
          a: "buying",
          successtip: "✓ VOWEL BEFORE Y!\nWhen vowel comes before Y (buy = bu-y), just add -ing! No changes needed! 💰"
        },
        {
          type: "mc",
          q: "Spelling check: Which are ALL correct?",
          options: [
            "wearing, putting, tying, shopping, making",
            "wearing, puting, tieing, shopping, makeing",
            "wearring, putting, tying, shoping, making"
          ],
          a: "wearing, putting, tying, shopping, making",
          successtip: "✓ ALL RULES!\nThis tests all spelling rules: simple +ing, doubling, -ie to -y, drop E! Master these! ✍️✨"
        }
      ]
    },

    // ========================================================================
    // SET 7: VIDEO CALL DESCRIPTION 💻
    // ========================================================================
    {
      id: "m7-u5-s7",
      title: "Set 7: Video Call - Describing Everyone 💻",
      context: `**Zoom Team Meeting**

Your boss asks everyone to describe what they're wearing today for a fun team-building activity. It's a casual Friday, so everyone is more relaxed than usual...`,
      
      questions: [
        {
          type: "mc",
          q: "You start: 'Hi everyone! I ___ a comfortable hoodie today since I'm working from home.'",
          options: [
            "am wearing",
            "wear",
            "wearing"
          ],
          a: "am wearing",
          successtip: "✓ WFH ATTIRE!\nWork-from-home fashion: comfortable on top, pajamas on bottom! 😄💻"
        },
        {
          type: "blank",
          q: "Colleague Sarah: 'I ___ my favorite striped sweater!' (wear + -ing)",
          a: "am wearing",
          accept: { ignoreCase: true, alts: ["'m wearing"] },
          successtip: "✓ CASUAL FRIDAY!\nCasual Friday = tradition in many offices, wear more relaxed clothes! 🎉👕"
        },
        {
          type: "mc",
          q: "Tom from IT: 'I ___ just a t-shirt. It's hot in my apartment!'",
          options: [
            "am wearing",
            "wear",
            "wearing"
          ],
          a: "am wearing",
          successtip: "✓ VIDEO CALL REALITY!\nZoom fashion = dress up on top, comfortable below the camera! 📹😅"
        },
        {
          type: "order",
          q: "Traduce: Mi jefe está usando una camisa de botones azul",
          a: "My boss is wearing a blue button-down shirt",
          successtip: "✓ BUTTON-DOWN!\n'Button-down shirt' = dress shirt with collar. Classic professional look! 👔"
        },
        {
          type: "mc",
          q: "Boss: 'Even on Zoom, I like to look professional. What about you, Maria? What ___ you ___?'",
          options: [
            "are / wearing",
            "is / wearing",
            "do / wear"
          ],
          a: "are / wearing",
          successtip: "✓ DIRECT QUESTION!\nQuestion form: 'What are you wearing?' = standard clothing question! ❓👗"
        },
        {
          type: "blank",
          q: "Maria: 'I ___ a nice blouse, but honestly... pajama pants below!' (wear + -ing)",
          a: "am wearing",
          accept: { ignoreCase: true, alts: ["'m wearing"] },
          successtip: "✓ ZOOM SECRET!\nUniversal WFH truth: professional on top, comfy on bottom! 😂🩳"
        },
        {
          type: "mc",
          q: "Everyone laughs. Boss: 'Actually, I ___ shorts too! Nobody can see!'",
          options: [
            "am wearing",
            "wear",
            "wearing"
          ],
          a: "am wearing",
          successtip: "✓ CONFESSION!\nEven bosses do it! Video calls changed dress codes forever! 📹👔🩳"
        },
        {
          type: "mc",
          q: "Intern Jake: 'I ___ my company t-shirt! Got it at last year's holiday party.'",
          options: [
            "am wearing",
            "wear",
            "wearing"
          ],
          a: "am wearing",
          successtip: "✓ COMPANY SWAG!\n'Company swag' = free branded clothing from your employer. Great for casual days! 🎁👕"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué están usando todos para la reunión?",
          a: "What is everyone wearing for the meeting?",
          successtip: "✓ GROUP QUESTION!\nRemember: 'everyone' = singular verb (is, not are)! Tricky grammar! 👥"
        },
        {
          type: "mc",
          q: "Summary: 'So today, most of us ___ casual clothes, even though it's a work day!'",
          options: [
            "are wearing",
            "is wearing",
            "wear"
          ],
          a: "are wearing",
          successtip: "✓ NEW NORMAL!\n'Most of us' = plural, so 'are wearing'. Video calls = casual revolution! 💻✨"
        }
      ]
    },

    // ========================================================================
    // SET 8: WEATHER REPORT & OUTFITS 🌧️☀️
    // ========================================================================
    {
      id: "m7-u5-s8",
      title: "Set 8: Weather Report - Dress for Success 🌦️",
      context: `**Morning News Weather Segment**

Reporter: "Good morning! I'm downtown where we're experiencing unpredictable weather. Let's see what people are wearing to deal with these conditions..."`,
      
      questions: [
        {
          type: "mc",
          q: "Reporter: 'This woman ___ a bright yellow raincoat. Smart choice!'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          successtip: "✓ RAINY DAY GEAR!\nYellow raincoat = high visibility + waterproof. Practical and stylish! 🌧️💛"
        },
        {
          type: "blank",
          q: "Reporter: 'And most people ___ umbrellas due to the rain.' (carry + -ing)",
          a: "are carrying",
          accept: { ignoreCase: true, alts: ["'re carrying"] },
          successtip: "✓ ESSENTIAL ACCESSORY!\nUmbrella = necessary in rainy cities. British are famous for always carrying one! ☂️🇬🇧"
        },
        {
          type: "mc",
          q: "Reporter: 'Wait - the sun is coming out! Now that man ___ his coat!'",
          options: [
            "is taking off",
            "takes off",
            "taking off"
          ],
          a: "is taking off",
          successtip: "✓ WEATHER CHANGE!\n'Take off' = remove clothing. Opposite of 'put on'. Phrasal verb! 🧥→👕"
        },
        {
          type: "order",
          q: "Traduce: La gente está usando bufandas y guantes por el frío",
          a: "People are wearing scarves and gloves because of the cold",
          successtip: "✓ WINTER ACCESSORIES!\nScarf + gloves = winter essentials. Keep neck and hands warm! 🧣🧤❄️"
        },
        {
          type: "mc",
          q: "Reporter: 'Interesting! Some people ___ layers so they can adjust.'",
          options: [
            "are wearing",
            "wears",
            "is wearing"
          ],
          a: "are wearing",
          successtip: "✓ LAYERING STRATEGY!\n'Wearing layers' = multiple thin pieces. Easy to adjust to changing temps! 🧥👔👕"
        },
        {
          type: "blank",
          q: "Reporter: 'This teenager ___ just a t-shirt in this cold! Young people...' (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ TEENAGE INVINCIBILITY!\nStereotype: Teenagers never feel cold! T-shirts in winter = universal phenomenon! 😄❄️"
        },
        {
          type: "mc",
          q: "Reporter: 'Professionals downtown ___ heavy coats and carrying briefcases.'",
          options: [
            "are wearing",
            "is wearing",
            "wears"
          ],
          a: "are wearing",
          successtip: "✓ BUSINESS ATTIRE!\nProfessionals = business people. Heavy coat + briefcase = classic office worker look! 💼🧥"
        },
        {
          type: "mc",
          q: "Reporter: 'For this weather, experts recommend you ___ waterproof clothing.' (advice, not current action)",
          options: [
            "wear",
            "are wearing",
            "wearing"
          ],
          a: "wear",
          tip: "⚠️ ADVICE VS. CURRENT ACTION!\n\nRecommendations/advice = simple present:\n✅ 'I recommend you wear...' (general advice)\n❌ 'I recommend you are wearing...' (wrong!)\n\nBUT describing current situation = continuous:\n✅ 'She is wearing a raincoat' (now)\n\nContext determines tense:\n• General truth/advice = simple\n• Happening now = continuous 🎯",
          successtip: "✓ GENERAL ADVICE!\nRecommendations use simple present, not continuous! Weather pro tip! 🌦️"
        },
        {
          type: "order",
          q: "Traduce: Él se está quitando su chaqueta porque hace calor",
          a: "He is taking off his jacket because it's hot",
          successtip: "✓ TOO HOT!\n'Taking off' = removing. 'Because' explains the reason = natural sentence structure! ☀️🔥"
        },
        {
          type: "mc",
          q: "Final report: 'As you can see, people ___ smartly for today's unpredictable weather!'",
          options: [
            "are dressing",
            "dress",
            "is dressing"
          ],
          a: "are dressing",
          successtip: "✓ SMART DRESSING!\n'Dress smartly' = dress intelligently. Right now observation = continuous! 🧠👗"
        }
      ]
    },

    // ========================================================================
    // SET 9: FASHION SHOW LIVE COMMENTARY 🎤✨
    // ========================================================================
    {
      id: "m7-u5-s9",
      title: "Set 9: Fashion Show - Live Commentary 🎤✨",
      context: `**MILAN FASHION WEEK - LIVE BROADCAST**

Commentator: "Welcome to Milan Fashion Week! The show is starting NOW. Models are walking the runway, and I'll describe everything they're wearing in real-time. This is so exciting!"`,
      
      questions: [
        {
          type: "mc",
          q: "Commentator: 'The first model ___ onto the runway! She looks absolutely stunning!'",
          options: [
            "is walking",
            "walks",
            "walking"
          ],
          a: "is walking",
          successtip: "✓ LIVE ACTION!\nFashion show commentary = perfect present continuous use! Happening RIGHT NOW! 🚶‍♀️✨"
        },
        {
          type: "blank",
          q: "Commentator: 'She ___ an elegant silk gown in deep red!' (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ LUXURY FABRIC!\nSilk gown = formal, elegant, expensive. Silk feels smooth and drapes beautifully! 👗✨"
        },
        {
          type: "mc",
          q: "Commentator: 'Notice the pattern - it ___ delicate stripes! Very sophisticated.'",
          options: [
            "has",
            "is having",
            "having"
          ],
          a: "has",
          tip: "⚠️ STATIVE VERB AGAIN!\n\n'Have' for possession/features = stative:\n✅ 'It has stripes' (simple present)\n❌ 'It is having stripes' (WRONG!)\n\nStative 'have' = NOT continuous!\n\nBUT 'having' CAN be continuous for activities:\n✅ 'I'm having dinner' (eating)\n✅ 'She's having a party' (hosting)\n\nContext matters! 🎯",
          successtip: "✓ FEATURE DESCRIPTION!\n'Has' (stative) describes permanent feature - not continuous! Pattern vocabulary! 🎨"
        },
        {
          type: "order",
          q: "Traduce: El segundo modelo está usando un traje a cuadros",
          a: "The second model is wearing a checked suit",
          successtip: "✓ CHECKED PATTERN!\n'Checked suit' = squares/plaid. Classic British style, trendy again! 🏴󠁧󠁢󠁥󠁮󠁧󠁿🕴️"
        },
        {
          type: "mc",
          q: "Commentator: 'He ___ leather shoes that match perfectly!'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          successtip: "✓ COORDINATED!\n'Match' = clothing colors/styles go well together. Fashion coordination! 👞✨"
        },
        {
          type: "blank",
          q: "Commentator: 'Models ___ extremely high heels - I don't know how they do it!' (wear + -ing)",
          a: "are wearing",
          accept: { ignoreCase: true, alts: ["'re wearing"] },
          successtip: "✓ RUNWAY HEELS!\nFashion show heels = often 5+ inches (12+ cm)! Models train to walk in them! 👠💪"
        },
        {
          type: "mc",
          q: "Commentator: 'Now here's something bold - polka dots! She ___ a white dress with black dots.'",
          options: [
            "is wearing",
            "wears",
            "wearing"
          ],
          a: "is wearing",
          successtip: "✓ POLKA DOT DRESS!\nPolka dots = playful, retro pattern. Black on white = classic combo! ⚪⚫👗"
        },
        {
          type: "mc",
          q: "Commentator: 'The designer ___ cotton with leather this season - interesting combination!'",
          options: [
            "is mixing",
            "mixes",
            "mixing"
          ],
          a: "is mixing",
          successtip: "✓ MATERIAL MIX!\nMixing materials = modern design trend. Contrast textures for interest! 🧵✨"
        },
        {
          type: "order",
          q: "Traduce: Ella está luciendo un vestido de seda liso precioso",
          a: "She is wearing a beautiful plain silk dress",
          successtip: "✓ ELEGANT SIMPLICITY!\nPlain silk = no pattern, lets the fabric quality shine! Less is more! 🌟"
        },
        {
          type: "mc",
          q: "Final model: 'And here comes the finale! She ___ the designer's signature piece - a gold lamé gown that ___ dazzling!'",
          options: [
            "is wearing / looks",
            "wears / is looking",
            "is wearing / is looking"
          ],
          a: "is wearing / looks",
          tip: "🎯 TWO DIFFERENT VERBS!\n\n'Wearing' = action, CAN be continuous:\n✅ 'She is wearing a gown' (now)\n\n'Looks' (appearance) = stative, NOT continuous:\n✅ 'It looks dazzling' (simple)\n❌ 'It is looking dazzling' (WRONG!)\n\nOne continuous, one simple!\n\nVerbs of appearance (look, seem, appear) are usually stative when describing how something appears! 🎯",
          successtip: "✓ GRAND FINALE!\n'Wearing' (continuous) + 'looks' (stative) = perfect combination! Signature piece = designer's best! 👗✨🥇"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE MASTERY - ALL INTEGRATED 🌟
    // ========================================================================
    {
      id: "m7-u5-s10",
      title: "Set 10: Complete Clothing Description Mastery 🌟",
      context: `**FINAL CHALLENGE**

You're a fashion blogger writing a live post about an event. Use everything you've learned: present continuous, vocabulary, materials, patterns, and spelling rules!`,
      
      questions: [
        {
          type: "mc",
          q: "'I ___ at a celebrity wedding right now, and everyone looks amazing!' Start your blog:",
          options: [
            "am",
            "is",
            "are"
          ],
          a: "am",
          successtip: "✓ BLOGGER VOICE!\n'I am' = first person, starting your live blog post! Personal perspective! 📝✨"
        },
        {
          type: "blank",
          q: "'The bride ___ a stunning white silk gown with delicate lace details.' (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ BRIDAL FASHION!\nWhite silk + lace = traditional bridal elegance. Lace = delicate fabric with holes/patterns! 👰✨"
        },
        {
          type: "mc",
          q: "'Her bridesmaids ___ matching pink dresses made of cotton.'",
          options: [
            "are wearing",
            "is wearing",
            "wears"
          ],
          a: "are wearing",
          successtip: "✓ COORDINATED PARTY!\nBridesmaids = bride's friends wearing matching dresses. Wedding tradition! 👗👗👗"
        },
        {
          type: "order",
          q: "Traduce: El novio está usando un elegante traje negro con corbata blanca",
          a: "The groom is wearing an elegant black suit with a white tie",
          successtip: "✓ GROOM'S ATTIRE!\nBlack suit + white tie = formal 'black tie' dress code. Very elegant! 🤵‍♂️✨"
        },
        {
          type: "mc",
          q: "'I notice many guests ___ striped and polka dot patterns today.'",
          options: [
            "are wearing",
            "is wearing",
            "wears"
          ],
          a: "are wearing",
          successtip: "✓ GUEST FASHION!\nWedding guests = invited people. Mix of patterns = festive, celebratory vibe! 🎉👗"
        },
        {
          type: "blank",
          q: "'One woman ___ a beautiful leather jacket over her dress - so chic!' (wear + -ing)",
          a: "is wearing",
          accept: { ignoreCase: true, alts: ["'s wearing"] },
          successtip: "✓ STYLE STATEMENT!\nLeather jacket + dress = edgy meets elegant. Modern fashion mixing! 🧥👗✨"
        },
        {
          type: "mc",
          q: "'The flower girls ___ adorable white dresses with pink bows.'",
          options: [
            "are wearing",
            "is wearing",
            "wears"
          ],
          a: "are wearing",
          successtip: "✓ FLOWER GIRLS!\nFlower girls = young girls who walk before bride, often throwing flower petals! 🌸👧"
        },
        {
          type: "mc",
          q: "Spelling check: 'Right now, someone ___ photos of everyone's outfits.' (take + -ing)",
          options: [
            "is taking",
            "is takeing",
            "is takking"
          ],
          a: "is taking",
          successtip: "✓ DROP SILENT E!\n'Taking' = 'take' drops the E before -ing. Wedding photographer at work! 📸"
        },
        {
          type: "mc",
          q: "'I ___ so many fashion trends at this wedding!' (see - experience, use present perfect)",
          options: [
            "have seen",
            "am seeing",
            "see"
          ],
          a: "have seen",
          successtip: "✓ EXPERIENCE COUNT!\n'Have seen' = present perfect for today's observations. Counting trends! 👁️✅"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: 'This wedding showcases everything: guests ___ cotton, silk, and leather materials, ___ stripes and polka dots, and everyone ___ their best outfits. The bride ___ stunning in her plain white gown, and I ___ every moment!' Fill all correctly:",
          options: [
            "are wearing / wearing / is wearing / looks / am enjoying",
            "wear / wear / wear / is looking / enjoy",
            "is wearing / wearing / are wearing / is looking / am enjoy"
          ],
          a: "are wearing / wearing / is wearing / looks / am enjoying",
          tip: "🎓 COMPLETE UNIT 5 MASTERY!\n\nThis tests EVERYTHING:\n\n✅ Present continuous formation:\n• 'Are wearing' (plural)\n• 'Wearing' (continuation)\n• 'Is wearing' (singular)\n• 'Am enjoying' (first person)\n\n✅ Stative vs. action verbs:\n• 'Looks' (stative - NOT continuous!)\n\n✅ Vocabulary:\n• Materials: cotton, silk, leather ✅\n• Patterns: stripes, polka dots ✅\n• Clothing: gown, outfits ✅\n\n✅ Grammar awareness:\n• When to use continuous\n• When NOT to use continuous\n\nAll Unit 5 concepts integrated! 🌟",
          successtip: "🎉 UNIT 5 COMPLETELY MASTERED! 🎉\n\n✅ PRESENT CONTINUOUS\n• Formation: BE + verb-ING ✅\n• Usage: actions happening NOW ✅\n• Questions: What are you wearing? ✅\n• Negatives: I'm not wearing... ✅\n\n✅ SPELLING RULES\n• Double consonant (putting, shopping) ✅\n• Drop silent E (making, taking) ✅\n• -ie to -y (tying, lying) ✅\n• Simple +ing (wearing, looking) ✅\n\n✅ CLOTHING VOCABULARY\n• Basic: shirt, pants, dress, shoes ✅\n• Formal: suit, tie, blazer ✅\n• Casual: jeans, hoodie, sneakers ✅\n• Weather: raincoat, scarf, gloves ✅\n\n✅ MATERIALS\n• Cotton, silk, leather, denim, wool ✅\n• 'Made of' structure mastered ✅\n\n✅ PATTERNS\n• Striped, checked, polka dot, plain ✅\n\n✅ PHRASAL VERBS\n• Put on, take off ✅\n\n✅ STATIVE VS. ACTION\n• Wear (can be continuous) ✅\n• Look/feel/have (usually NOT) ✅\n\n✅ REAL-WORLD CONTEXTS\n• Park, office, police, shopping ✅\n• Weather, fashion shows, video calls ✅\n• Weddings, events ✅\n\n🌟 YOU CAN NOW:\n• Describe any clothing situation ✅\n• Use present continuous perfectly ✅\n• Spell -ing forms correctly ✅\n• Talk about materials & patterns ✅\n• Sound natural and fluent ✅\n\nB1-B2 clothing & present continuous = MASTERED! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - A DAY IN DIFFERENT SITUATIONS (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u5-reading",
    title: "Reading Practice - A Day in Different Outfits",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Morning - Getting Dressed (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**A Day in Different Outfits**

**7:00 AM - Getting Ready**

I'm getting dressed for an important day. I'm putting on my favorite blue suit made of wool. I'm tying my red tie carefully. My wife is wearing her elegant gray dress. We're both getting ready for a client presentation.`
      },
      {
        type: "mc",
        q: "What tense is primarily used in this passage?",
        options: [
          "Present continuous (am getting, is wearing)",
          "Present simple",
          "Past continuous"
        ],
        a: "Present continuous (am getting, is wearing)",
        successtip: "✓ LIVE ACTION!\nPresent continuous = perfect for describing getting dressed RIGHT NOW! ⏰👔"
      },
      {
        type: "blank",
        q: "What material is the suit made of?",
        a: "wool",
        accept: { ignoreCase: true },
        successtip: "✓ WOOL SUIT!\nWool = professional fabric, keeps shape well. Perfect for business! 🧥💼"
      },

      // ======================================================================
      // BLOQUE 2: At Work - Formal Attire (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**10:00 AM - At the Office**

The presentation is going well. I'm standing confidently in my suit. My colleague Sarah is wearing a professional black blazer with a white shirt. Our clients are wearing casual clothes - one has a checked shirt, another is wearing jeans.`
      },
      {
        type: "mc",
        q: "The text uses 'is wearing' multiple times. This is because:",
        options: [
          "It's describing what people are wearing at this moment",
          "It's a grammar mistake - should be 'wears'",
          "It's talking about habits"
        ],
        a: "It's describing what people are wearing at this moment",
        successtip: "✓ CURRENT DESCRIPTION!\nPresent continuous = describing the meeting happening NOW! 💼✨"
      },
      {
        type: "mc",
        q: "What pattern is mentioned?",
        options: [
          "Checked (cuadros)",
          "Striped (rayas)",
          "Polka dot (lunares)"
        ],
        a: "Checked (cuadros)",
        successtip: "✓ PATTERN VOCABULARY!\nChecked shirt = casual business look, less formal than plain! 🔲👔"
      },

      // ======================================================================
      // BLOQUE 3: Lunch Break - Casual Change (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**1:00 PM - Lunch Break**

I'm taking off my tie - too formal for lunch! I'm walking to a nearby café. Outside, people are wearing all kinds of outfits. Some are wearing shorts because it's hot. One woman is carrying a beautiful leather handbag.`
      },
      {
        type: "mc",
        q: "'Taking off' my tie means:",
        options: [
          "Removing my tie",
          "Putting on my tie",
          "Buying a tie"
        ],
        a: "Removing my tie",
        successtip: "✓ PHRASAL VERB!\n'Take off' = remove clothing. Opposite: 'put on' = wear clothing! 🔄"
      },
      {
        type: "blank",
        q: "What material is the handbag made of?",
        a: "leather",
        accept: { ignoreCase: true },
        successtip: "✓ LEATHER ACCESSORY!\nLeather bags = durable and stylish. Higher quality than synthetic! 👜✨"
      },

      // ======================================================================
      // BLOQUE 4: Shopping - Trying Clothes (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**4:00 PM - Quick Shopping**

After work, I'm shopping for casual clothes. I'm trying on a comfortable cotton t-shirt with stripes. It fits perfectly! The store clerk is wearing a name tag and helping everyone. She's showing me different patterns - plain, checked, and polka dot options.`
      },
      {
        type: "mc",
        q: "How many patterns are mentioned in this passage?",
        options: [
          "Four: striped, plain, checked, polka dot",
          "Only one",
          "Two"
        ],
        a: "Four: striped, plain, checked, polka dot",
        successtip: "✓ ALL PATTERNS!\nThis passage includes all Unit 5 pattern vocabulary! 🎨✅"
      },
      {
        type: "mc",
        q: "'I'm trying on a t-shirt' - what does this mean?",
        options: [
          "Putting it on temporarily to see if it fits",
          "Buying the t-shirt",
          "Looking at the t-shirt"
        ],
        a: "Putting it on temporarily to see if it fits",
        successtip: "✓ SHOPPING VERB!\n'Try on' = test clothing before buying. Essential shopping phrase! 🛍️"
      },

      // ======================================================================
      // BLOQUE 5: Evening Event - Elegant Outfit + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**7:00 PM - Dinner Party**

I'm wearing my new striped shirt with black pants. My wife is looking elegant in a silk dress. Other guests are wearing various outfits - some formal, some casual. Everyone is enjoying the evening and complimenting each other's clothes. Perfect end to the day!`
      },
      {
        type: "mc",
        q: "'My wife is looking elegant' - why is 'looking' used here, not 'looks'?",
        options: [
          "Actually, 'looks' would be more natural here (stative verb)",
          "'Looking' is correct - continuous is always better",
          "Both are equally correct"
        ],
        a: "Actually, 'looks' would be more natural here (stative verb)",
                tip: "⚠️ SUBTLE GRAMMAR POINT!\n\n'Look' for appearance is usually stative:\n✅ 'She looks elegant' (MORE NATURAL)\n⚠️ 'She is looking elegant' (less common)\n\nBUT both can be used!\n\n'Is looking' emphasizes the current moment more, but natives usually prefer 'looks' for general appearance.\n\nIn the reading, it's used for variety, but 'looks' would be the standard choice! 🎯",
        successtip: "✓ GRAMMAR NUANCE!\nMost natives would say 'looks elegant' (stative) rather than 'is looking' - but both work! 👗✨"
      },
      {
        type: "mc",
        q: "Throughout this entire reading, what grammar structure was used most frequently?",
        options: [
          "Present continuous (am/is/are + verb-ing)",
          "Present simple",
          "Past simple"
        ],
        a: "Present continuous (am/is/are + verb-ing)",
        successtip: "✓ CONSISTENT TENSE!\nPresent continuous throughout = describing actions happening in each moment! ⏰✅"
      },
      {
        type: "blank",
        q: "How many different materials were mentioned? List them.",
        a: "wool, leather, cotton, silk",
        accept: { ignoreCase: true, alts: ["wool leather cotton silk", "4 materials", "four materials"] },
        successtip: "✓ ALL MATERIALS!\nWool, leather, cotton, silk = all Unit 5 material vocabulary in context! 🧵✨"
      },
      {
        type: "mc",
        q: "FINAL QUESTION: This reading showed present continuous in multiple real-life contexts. Which were included?",
        options: [
          "All of these: getting dressed, working, shopping, social event",
          "Only formal situations",
          "Only one context"
        ],
        a: "All of these: getting dressed, working, shopping, social event",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ PRESENT CONTINUOUS\n• Getting dressed (putting on) ✅\n• Current descriptions (wearing, carrying) ✅\n• Shopping actions (trying on) ✅\n• Live situations (standing, showing) ✅\n\n✅ CLOTHING VOCABULARY\n• Formal: suit, tie, blazer, dress ✅\n• Casual: jeans, shorts, t-shirt ✅\n• Accessories: handbag, name tag ✅\n\n✅ MATERIALS\n• Wool, leather, cotton, silk ✅\n• 'Made of' structure ✅\n\n✅ PATTERNS\n• Striped, checked, plain, polka dot ✅\n\n✅ PHRASAL VERBS\n• Putting on, taking off, trying on ✅\n\n✅ REALISTIC CONTEXTS\n• Morning routine ✅\n• Business meeting ✅\n• Lunch break ✅\n• Shopping ✅\n• Social event ✅\n\n🌟 ALL UNIT 5 CONCEPTS IN ACTION!\nGrammar + Vocabulary = Natural English! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 5 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u5-review",
    title: "⭐ Unit 5 Review: What I'm Wearing",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which sentence uses present continuous CORRECTLY?",
        options: [
          "She is wearing a beautiful dress right now",
          "She wearing a beautiful dress right now",
          "She is wear a beautiful dress right now"
        ],
        a: "She is wearing a beautiful dress right now",
        tip: "📐 REVIEW: PRESENT CONTINUOUS!\n\nStructure: BE + verb-ING\n\n✅ She IS wearing (BE + verb-ING)\n❌ She wearing (missing BE)\n❌ She is wear (missing -ING)\n\nAll three parts needed:\n1. Subject (She)\n2. BE verb (is)\n3. Verb-ING (wearing)\n\nThis is Unit 5's core grammar! 🎯",
        successtip: "✓ PERFECT FORMATION!\nBonus: 'Right now' is a key signal for present continuous! ⏰"
      },
      
      {
        type: "blank",
        q: "Spelling rule: 'shop' → '___' (add -ing with correct spelling)",
        a: "shopping",
        accept: { ignoreCase: true },
        successtip: "✓ DOUBLE CONSONANT!\nCVC pattern (sh-o-p) = double the P! Other examples: stopping, running, sitting! ✍️"
      },
      
      {
        type: "mc",
        q: "A jacket made from animal hide is made of:",
        options: [
          "Leather",
          "Cotton",
          "Silk"
        ],
        a: "Leather",
        successtip: "✓ LEATHER!\nFun fact: 'Genuine leather' = real animal hide. 'Faux leather' = synthetic/fake! 🐄👜"
      },
      
      {
        type: "order",
        q: "Traduce: ¿Qué estás usando para la fiesta?",
        a: "What are you wearing for the party?",
        successtip: "✓ PARTY OUTFIT QUESTION!\n'What are you wearing?' = most common way to ask about someone's clothing! 🎉👗"
      },
      
      {
        type: "mc",
        q: "Which pattern has round dots?",
        options: [
          "Polka dot",
          "Striped",
          "Checked"
        ],
        a: "Polka dot",
        successtip: "✓ POLKA DOT!\nOrigin: Named after the 'polka' dance from the 1800s! Classic retro pattern! 🔴⚪💃"
      },
      
      {
        type: "blank",
        q: "Fix the spelling: 'I am makeing breakfast' → 'I am ___ breakfast'",
        a: "making",
        accept: { ignoreCase: true },
        successtip: "✓ DROP SILENT E!\n'Make' loses the E before -ing. Same for: taking, having, coming, writing! 📝"
      },
      
      {
        type: "mc",
        q: "'He looks good in that suit' - why NOT 'is looking'?",
        options: [
          "Because 'look' (appearance) is a stative verb",
          "Because it's wrong grammar",
          "Because he's not moving"
        ],
        a: "Because 'look' (appearance) is a stative verb",
        successtip: "✓ STATIVE VERB!\nVerbs of appearance (look, seem, appear) usually don't use continuous! 👀"
      },
      
      {
        type: "mc",
        q: "Correct spelling: 'tie' + '-ing' =",
        options: [
          "tying",
          "tieing",
          "tying"
        ],
        a: "tying",
        tip: "✍️ RARE SPELLING RULE!\n\nVerbs ending in -IE:\n• tie → tying\n• lie → lying\n• die → dying\n\nWhy? -ie changes to -y before -ing!\n\n❌ tieing = WRONG\n✅ tying = CORRECT\n\nOnly a few verbs follow this rule, but they're common words! 🎯",
        successtip: "✓ TRICKY SPELLING!\n-IE to -Y rule = rare but important! Memorize: tie/lie/die all become -ying! 👔"
      },
      
      {
        type: "order",
        q: "Traduce: Él se está quitando su abrigo porque hace calor",
        a: "He is taking off his coat because it's hot",
        successtip: "✓ PHRASAL VERB!\n'Take off' (remove) vs 'put on' (wear) = opposite actions! Both common! 🧥🔄"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY: Identify ALL correct elements in this sentence: 'I'm wearing a comfortable cotton shirt with stripes, and I'm putting on my leather jacket because it's getting cold.'",
        options: [
          "All correct: present continuous (2x), material (2x), pattern (1x), phrasal verb, spelling",
          "Only grammar is correct",
          "Multiple errors"
        ],
        a: "All correct: present continuous (2x), material (2x), pattern (1x), phrasal verb, spelling",
        tip: "🎓 COMPLETE UNIT 5 INTEGRATION!\n\nThis sentence has EVERYTHING:\n\n✅ Present continuous (2 uses):\n• 'I'm wearing' (BE + wearing)\n• 'I'm putting on' (BE + putting on)\n• 'it's getting' (BE + getting)\n\n✅ Materials (2):\n• Cotton\n• Leather\n\n✅ Pattern (1):\n• Stripes\n\n✅ Phrasal verb:\n• 'Putting on'\n\n✅ Spelling rules:\n• 'Wearing' (simple +ing)\n• 'Putting' (double T)\n• 'Getting' (double T)\n\nAll Unit 5 concepts perfectly integrated! 🌟",
        successtip: "🎉 UNIT 5 COMPLETELY MASTERED! 🎉\n\n✅ PRESENT CONTINUOUS\n• Formation: BE + verb-ING ✅\n• Usage: actions happening NOW ✅\n• Questions & negatives ✅\n• Time expressions (right now, currently) ✅\n\n✅ SPELLING RULES (-ING)\n1. Double consonant: putting, shopping ✅\n2. Drop silent E: making, taking ✅\n3. -IE to -Y: tying, lying ✅\n4. Simple +ING: wearing, looking ✅\n\n✅ CLOTHING VOCABULARY\n• Formal: suit, tie, blazer, dress shoes ✅\n• Casual: jeans, t-shirt, hoodie, sneakers ✅\n• Accessories: scarf, belt, gloves ✅\n• Weather: raincoat, umbrella, coat ✅\n\n✅ MATERIALS\n• Cotton (breathable) ✅\n• Silk (luxurious) ✅\n• Leather (durable) ✅\n• Denim (for jeans) ✅\n• Wool (warm) ✅\n\n✅ PATTERNS\n• Striped (rayas) ✅\n• Checked (cuadros) ✅\n• Polka dot (lunares) ✅\n• Plain (liso) ✅\n\n✅ PHRASAL VERBS\n• Put on (wear) ✅\n• Take off (remove) ✅\n• Try on (test clothing) ✅\n\n✅ STATIVE VS. ACTION VERBS\n• Wear/carry/put on (CAN be continuous) ✅\n• Look/feel/have features (usually NOT) ✅\n\n✅ REAL-WORLD CONTEXTS\n• Park, office, police reports ✅\n• Shopping, weather, video calls ✅\n• Fashion shows, events, daily life ✅\n\n🌟 YOU CAN NOW:\n• Describe what anyone is wearing ✅\n• Use present continuous perfectly ✅\n• Spell all -ING forms correctly ✅\n• Discuss materials and patterns ✅\n• Sound natural and confident ✅\n\nB1-B2 clothing & present continuous = MASTERED! 💪\n\n¡Excelente trabajo! Ready for Unit 6! 🚀"
      }
    ]
  }
};
